import { SettingsSummaryDto, UserSettingsDto, ContactDto, InvitationDto, ListManagementDto } from '../types/settings';
import { apiCall } from './authApi';

// Settings Summary
export const getSettingsSummary = async (): Promise<SettingsSummaryDto> => {
  const response = await apiCall('settings/summary');
  if (!response.ok) {
    throw new Error('Failed to fetch settings summary');
  }
  return response.json();
};

// User Settings
export const getUserSettings = async (): Promise<UserSettingsDto> => {
  const response = await apiCall('settings/user');
  if (!response.ok) {
    throw new Error('Failed to fetch user settings');
  }
  return response.json();
};

export const updateUserSettings = async (settings: Partial<UserSettingsDto>): Promise<UserSettingsDto> => {
  const response = await apiCall('settings/user', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
  if (!response.ok) {
    throw new Error('Failed to update user settings');
  }
  return response.json();
};

// Contact Management
export const getContacts = async (): Promise<ContactDto[]> => {
  const response = await apiCall('settings/contacts');
  if (!response.ok) {
    throw new Error('Failed to fetch contacts');
  }
  return response.json();
};

export const removeContact = async (contactId: number): Promise<void> => {
  const response = await apiCall(`settings/contacts/${contactId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Failed to remove contact');
  }
};

// Invitation Management
export const getSentInvitations = async (): Promise<InvitationDto[]> => {
  const response = await apiCall('settings/invitations/sent');
  if (!response.ok) {
    throw new Error('Failed to fetch sent invitations');
  }
  return response.json();
};

export const getReceivedInvitations = async (): Promise<InvitationDto[]> => {
  const response = await apiCall('settings/invitations/received');
  if (!response.ok) {
    throw new Error('Failed to fetch received invitations');
  }
  return response.json();
};

// Use inviteUser for invitation creation, matching the previous createInvitation signature
export const createInvitation = async (invitation: { 
  inviteeEmail: string; 
  inviteeName: string; 
  message?: string;
  selectedListIds?: number[];
}): Promise<void> => {
  return inviteUser(
    invitation.inviteeEmail, 
    invitation.inviteeName, 
    invitation.message, 
    invitation.selectedListIds
  );
};

export const inviteUser = async (inviteeEmail: string, inviteeName: string, personalMessage?: string, selectedListIds?: number[]): Promise<void> => {
  const response = await apiCall('invitations', {
    method: 'POST',
    body: JSON.stringify({ 
      inviteeEmail, 
      inviteeName, 
      personalMessage: personalMessage || null,
      selectedListIds: selectedListIds || []
    })
  });
  if (!response.ok) {
    let errorMsg = 'Failed to send invitation';
    try {
      const errorData = await response.json();
      errorMsg = errorData.message || errorMsg;
    } catch {
      // Failed to parse error response, use default message
    }
    throw new Error(errorMsg);
  }
  return response.json();
};

export const updateInvitationStatus = async (
  invitationId: number, 
  status: 'accepted' | 'declined' | 'cancelled'
): Promise<void> => {
  const response = await apiCall(`settings/invitations/${invitationId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    throw new Error('Failed to update invitation status');
  }
};

// List Management
export const getManageableLists = async (): Promise<ListManagementDto[]> => {
  const response = await apiCall('settings/lists');
  if (!response.ok) {
    throw new Error('Failed to fetch manageable lists');
  }
  return response.json();
};

export const updateListSettings = async (
  listId: number, 
  settings: Partial<ListManagementDto>
): Promise<ListManagementDto> => {
  const response = await apiCall(`settings/lists/${listId}`, {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
  if (!response.ok) {
    throw new Error('Failed to update list settings');
  }
  return response.json();
};
